/**
 * Host Application Modal – Multi-step form navigation
 *
 * Steps:
 *   1  Partnership overview  (no inputs; introduction)
 *   2  Personal information  (name, email, phone)
 *   3  Venue & hosting       (city, venue type, group size, experience)
 *   4  Preferences & submit  (preferred workshops, message; submit button)
 *
 * Form submission is handled by assets/modal-form-handler.js (Formspark).
 * This file only controls step visibility, validation, and navigation UI.
 */
(function () {
  'use strict';

  var MODAL_ID    = 'kraft-host-application-modal';
  var TOTAL_STEPS = 4;

  var STEP_CONTENT = [
    {
      title:    'Become a Host',
      subtitle: 'Partner with KraftStories to lead creative workshops'
    },
    {
      title:    'About you',
      subtitle: 'We\u2019ll use this to get in touch — takes about 30 seconds.'
    },
    {
      title:    'Your venue',
      subtitle: 'Tell us about where and how you\u2019d like to host.'
    },
    {
      title:    'Almost there',
      subtitle: 'Pick your preferred workshop type and submit.'
    }
  ];

  var NEXT_LABELS = [
    'Tell us about you',
    'Venue details',
    'Final step',
    ''
  ];

  var state = { currentStep: 1 };

  function getModal() {
    return document.getElementById(MODAL_ID);
  }

  function goToStep(step) {
    var modal = getModal();
    if (!modal) return;

    state.currentStep = step;

    modal.querySelectorAll('.kraft-host-app-step').forEach(function (el) {
      el.classList.toggle('active', parseInt(el.getAttribute('data-host-app-step'), 10) === step);
    });

    var titleEl    = modal.querySelector('[data-host-app-step-title]');
    var subtitleEl = modal.querySelector('[data-host-app-step-subtitle]');
    if (titleEl)    titleEl.textContent    = STEP_CONTENT[step - 1].title;
    if (subtitleEl) subtitleEl.textContent = STEP_CONTENT[step - 1].subtitle;

    var countEl = modal.querySelector('[data-host-app-step-count]');
    if (countEl) countEl.textContent = 'Step ' + step + ' of ' + TOTAL_STEPS;

    var progressFill = modal.querySelector('[data-host-app-progress]');
    if (progressFill) {
      progressFill.style.width = ((step / TOTAL_STEPS) * 100) + '%';
    }

    var backBtn = modal.querySelector('[data-host-app-back]');
    if (backBtn) {
      backBtn.classList.toggle('visible', step > 1);
    }

    var nextWrap = modal.querySelector('.kraft-host-app-next-wrap');
    if (nextWrap) {
      nextWrap.classList.toggle('is-hidden', step === TOTAL_STEPS);
    }

    var nextLabel = modal.querySelector('[data-host-app-next-label]');
    if (nextLabel && NEXT_LABELS[step - 1]) {
      nextLabel.textContent = NEXT_LABELS[step - 1];
    }

    var footer = modal.querySelector('.kraft-host-app-footer');
    if (footer) {
      footer.style.display = step === TOTAL_STEPS ? 'none' : '';
    }

    clearErrors(modal, step);
  }

  /* ── Validation ── */

  function validateStep(step) {
    var modal = getModal();
    if (!modal) return true;

    var stepEl = modal.querySelector('[data-host-app-step="' + step + '"]');
    if (!stepEl) return true;

    var requiredFields = stepEl.querySelectorAll('[data-host-app-required]');
    var valid = true;

    requiredFields.forEach(function (field) {
      removeFieldError(field);
      var value = field.value.trim();
      if (!value) {
        showFieldError(field, getFieldErrorMessage(field));
        valid = false;
      } else if (field.type === 'email' && !isValidEmail(value)) {
        showFieldError(field, 'Please enter a valid email address.');
        valid = false;
      }
    });

    return valid;
  }

  function getFieldErrorMessage(field) {
    var placeholder = field.getAttribute('placeholder') || field.tagName.toLowerCase();
    if (field.type === 'email') return 'Email is required.';
    if (field.tagName.toLowerCase() === 'select') return 'Please make a selection.';
    return placeholder.replace(/\s*\(optional\)\s*$/i, '').trim() + ' is required.';
  }

  function isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  function showFieldError(field, message) {
    field.classList.add('kraft-host-app-field-error');
    var errorEl = document.createElement('p');
    errorEl.className = 'kraft-host-app-error-msg';
    errorEl.textContent = message;
    field.parentNode.insertBefore(errorEl, field.nextSibling);
  }

  function removeFieldError(field) {
    field.classList.remove('kraft-host-app-field-error');
    var next = field.nextSibling;
    if (next && next.classList && next.classList.contains('kraft-host-app-error-msg')) {
      next.parentNode.removeChild(next);
    }
  }

  function clearErrors(modal, step) {
    var stepEl = modal.querySelector('[data-host-app-step="' + step + '"]');
    if (!stepEl) return;
    stepEl.querySelectorAll('.kraft-host-app-field-error').forEach(function (el) {
      el.classList.remove('kraft-host-app-field-error');
    });
    stepEl.querySelectorAll('.kraft-host-app-error-msg').forEach(function (el) {
      el.parentNode && el.parentNode.removeChild(el);
    });
  }

  /* ── Reset on open ── */

  function resetModal() {
    var modal = getModal();
    if (!modal) return;

    var form = modal.querySelector('form');
    if (form) form.reset();

    modal.querySelectorAll('.kraft-host-app-field-error').forEach(function (el) {
      el.classList.remove('kraft-host-app-field-error');
    });
    modal.querySelectorAll('.kraft-host-app-error-msg').forEach(function (el) {
      el.parentNode && el.parentNode.removeChild(el);
    });

    var success = modal.querySelector('.kraft-modal-success');
    if (success) success.style.display = 'none';
    if (form) form.style.display = '';

    goToStep(1);
  }

  /* ── Initialise ── */

  function initModal() {
    var modal = getModal();
    if (!modal) return;

    var nextBtn = modal.querySelector('[data-host-app-next]');
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        if (!validateStep(state.currentStep)) return;
        if (state.currentStep < TOTAL_STEPS) {
          goToStep(state.currentStep + 1);
        }
      });
    }

    var backBtn = modal.querySelector('[data-host-app-back]');
    if (backBtn) {
      backBtn.addEventListener('click', function () {
        if (state.currentStep > 1) {
          goToStep(state.currentStep - 1);
        }
      });
    }

    modal.querySelectorAll('[data-host-app-required]').forEach(function (field) {
      field.addEventListener('input', function () { removeFieldError(field); });
      field.addEventListener('change', function () { removeFieldError(field); });
    });
  }

  /* ── Per-trigger modal image ── */

  function getLeftArea() {
    var m = getModal();
    return m ? m.querySelector('#host-application-modal-left-area') : null;
  }

  function applyModalImage(imageUrl, imageText) {
    var leftArea = getLeftArea();
    if (!leftArea) return;

    var img = leftArea.querySelector('.kraft-host-app-modal-img');
    var textEl = leftArea.querySelector('#host-application-modal-left-text');

    if (img) {
      img.src = imageUrl || '';
      img.removeAttribute('srcset');
      img.style.display = imageUrl ? '' : 'none';
    }
    if (textEl) {
      textEl.textContent = imageText || '';
      textEl.style.display = imageText ? '' : 'none';
    }

    leftArea.classList.toggle('kraft-modal-left-area--empty', !imageUrl);
  }

  function resetToDefaultImage() {
    var modal = getModal();
    if (!modal) return;
    var defaultImage = modal.getAttribute('data-host-app-default-image') || '';
    var defaultText = modal.getAttribute('data-host-app-default-image-text') || '';
    applyModalImage(defaultImage, defaultText);
  }

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-modal-trigger="' + MODAL_ID + '"]');
    if (!trigger) return;

    var modal = getModal();
    if (!modal) return;
    var defaultImage = modal.getAttribute('data-host-app-default-image') || '';
    var defaultText = modal.getAttribute('data-host-app-default-image-text') || '';

    var triggerImage = trigger.getAttribute('data-modal-image');
    var triggerText = trigger.getAttribute('data-modal-image-text');

    var imageUrl = triggerImage || defaultImage;
    var imageText = triggerImage ? (triggerText || '') : defaultText;

    applyModalImage(imageUrl, imageText);
  }, true);

  (function setupCloseObserver() {
    var modal = getModal();
    if (!modal) return;
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.attributeName === 'class' && !modal.classList.contains('active')) {
          resetToDefaultImage();
        }
      });
    });
    observer.observe(modal, { attributes: true, attributeFilter: ['class'] });
  })();

  window.addEventListener('modal:opened', function (e) {
    if (e.detail && e.detail.modalId === MODAL_ID) {
      resetModal();
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initModal);
  } else {
    initModal();
  }

})();
